/**
 * @file homeFirstRun.tsx
 * @brief Calm Home pieces for a user with no meetings yet (DES 19-10-2026):
 *        the "Try a demo meeting" card and the blurred example meetings
 *        behind "Your meetings will appear here."
 *
 * @details
 * The example rows are the real Home meeting card with made-up rows, blurred,
 * hidden from screen readers and not clickable: they only show what the rail
 * will look like. The demo card opens the built-in demo meeting
 * (features/demo-meeting), which is free and read-only.
 *
 * Related: ./HomePage.tsx, ../demo-meeting/DemoMeetingPage.tsx
 *
 * @author DES
 * @date 20-10-2026
 */

import { Link } from "react-router-dom";
import { ArrowRight, FlaskConical } from "lucide-react";
import type { MeetingRow } from "@/features/company-meetings/schemas";
import { CARD_W, MeetingCard } from "./homeCards";

const DAY = 24 * 60 * 60 * 1000;

function exampleRows(now = Date.now()): MeetingRow[] {
  const row = (id: string, title: string, daysAgo: number, mins: number, people: number): MeetingRow => ({
    id,
    title,
    started_at: new Date(now - daysAgo * DAY).toISOString(),
    status: "ready",
    total_duration_seconds: mins * 60,
    participants: Array.from({ length: people }, (_, i) => ({ name: `Person ${i + 1}` })),
  });
  return [
    row("ex1", "Weekly team sync", 1, 32, 5),
    row("ex2", "Client kick-off", 3, 48, 3),
    row("ex3", "Budget review", 6, 21, 4),
  ];
}

export function DemoMeetingCard({ to, state }: { to: string; state?: unknown }) {
  return (
    <section className="mb-6 px-5">
      <Link
        to={to}
        state={state}
        className="flex items-center gap-3 rounded-2xl bg-black/[0.04] px-4 py-3.5 transition-colors hover:bg-black/[0.07] dark:bg-white/[0.06] dark:hover:bg-white/[0.1]"
      >
        <FlaskConical className="h-6 w-6 shrink-0 text-brand" strokeWidth={1.5} />
        <span className="min-w-0 flex-1">
          <span className="block text-[17px] font-bold text-app-text">Try a demo meeting</span>
          <span className="block text-sm text-app-text-secondary">
            See the notes, decisions, to-dos, speakers and Ask AI from a short sample meeting. Free,
            nothing to record.
          </span>
        </span>
        <ArrowRight className="h-5 w-5 shrink-0 text-app-text-secondary" strokeWidth={1.75} />
      </Link>
    </section>
  );
}

export function EmptyMeetingsPreview() {
  return (
    <div className="relative px-5">
      <div
        aria-hidden
        className="pointer-events-none flex select-none gap-3 overflow-hidden opacity-60 blur-[3px]"
        // Never focusable: the cards are links.
        inert
      >
        {exampleRows().map((m) => (
          <div key={m.id} style={{ width: CARD_W }} className="shrink-0">
            <MeetingCard meeting={m} to="#" />
          </div>
        ))}
      </div>
      <p className="absolute inset-0 flex items-center justify-center px-5 text-center text-[15px] font-semibold text-app-text">
        Your meetings will appear here.
      </p>
    </div>
  );
}
